import { Component, inject, input, output, effect, signal, computed, resource, ChangeDetectionStrategy } from '@angular/core';
import { TransportOutConfiguration } from '../models/transport-configuration';
import { TransportService } from '../services/transport.service';
import { TransportType } from '../models/transport-type';
import { SingleselectboxComponent } from 'src/app/controls/components/basic/singleselectbox/singleselectbox.component';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'ep-transport-configuration-selector',
    templateUrl: './transport-configuration-selector.component.html',
    styleUrls: ['./transport-configuration-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, SingleselectboxComponent]
})

export class TransportConfigurationSelectorComponent {

    private transportService = inject(TransportService);

    ignoreEmptyPartner = input<boolean>(false);
    partnerId = input<number>(0);
    selectFirstItemIfOnlyOne = input<boolean>(false);
    transportConfiguration = input<TransportOutConfiguration>(null);
    includeUniversal = input<boolean>(true);

    /**
     * @deprecated This input property is deprecated and will be removed in future versions.
     */
    disabled = input<boolean>(false);

    selectedtransportConfiguration = signal<TransportOutConfiguration>(null);
    searchExprOption = signal<string[]>(['Name', 'TransportTypeName', 'DisplayAddress']);

    transportConfigurationChange = output<TransportOutConfiguration>();

    transportTypesResource = resource({
        params: () => ({}),
        loader: () => this.getTransportTypes()
    });
    transportTypes = computed(() => this.transportTypesResource.value() ?? []);

    transportConfigurationsResource = resource({
        params: () => ({ ignoreEmptyPartner: this.ignoreEmptyPartner(), partnerId: this.partnerId(), transportTypes: this.transportTypes() }),
        loader: ({ params }) => this.getTransportConfigurations(params.ignoreEmptyPartner, params.partnerId, params.transportTypes)
    });
    transportConfigurations = computed(() => this.transportConfigurationsResource.value() ?? []);

    dropDownDataSourceResource = resource({
        params: () => this.transportConfigurations(),
        loader: ({ params }) => this.createDropDownDataSource(params)
    });
    dropDownDataSource = computed(() => this.dropDownDataSourceResource.value() ?? []);

    constructor() {
        effect(() => {
            this.selectedtransportConfiguration.set(this.transportConfiguration());
            this.transportConfigurationChange.emit(this.transportConfiguration());
        });
    }

    onValueChange(e: number) {
        this.selectedtransportConfiguration.set(this.transportConfigurations().find(tc => tc.Id === e) || null);
        this.transportConfigurationChange.emit(this.selectedtransportConfiguration());
    }

    async getTransportConfigurations(ignoreEmptyPartner: boolean, partnerId: number, transportTypes: TransportType[]): Promise<TransportOutConfiguration[]> {
        if (!ignoreEmptyPartner && !partnerId) {
            return [];
        }

        try {
            const result = await this.transportService.getPartnerTransportOutConfigurations(this.partnerId(), this.includeUniversal());
            if (!result) {
                return [];
            }

            const updatedResult = result.map(t => ({
                ...t,
                TransportTypeName: transportTypes.find(tt => tt.Id === t.TransportTypeId)?.Name
            }));

            if (updatedResult.length === 1 && this.selectFirstItemIfOnlyOne()) {
                this.transportConfigurationChange.emit(updatedResult[0]);
            }

            return updatedResult;
        } catch (error) {
            console.error('Error fetching transport configurations:', error);
            return [];
        }
    }

    async getTransportTypes(): Promise<TransportType[]> {
        return await this.transportService.getTransportTypes(this.transportService.transportTypesOut);
    }

    createDropDownDataSource(transportConfigurations: TransportOutConfiguration[]): Promise<LocationGroup[]> {
        return new Promise((resolve) => {

            if (!transportConfigurations) {
                resolve([]);
                return;
            }

            const sortByName = (a: TransportOutConfiguration, b: TransportOutConfiguration) => a.Name.localeCompare(b.Name);
            const partnerLocations = transportConfigurations
                .filter(t => !!t?.PartnerId)
                .sort(sortByName);

            const globalLocations = transportConfigurations
                .filter(t => !t?.PartnerId)
                .sort(sortByName);

            resolve([
                new LocationGroup('Partner locations', partnerLocations),
                new LocationGroup('Global locations', globalLocations)
            ]);
        });
    }
}

class LocationGroup {
    constructor(public key: string, public items: TransportOutConfiguration[]) { }
}
